/**
 * mk-whats-in-a-box — looping box video, played only while on screen.
 *
 * Replaced the task-10 scroll-scrubbed sprite sequence at task 21. Nothing here
 * is per-frame, so this section NO LONGER subscribes to the scroll engine's
 * rAF loop. It uses only the engine's observe(), which picks the correct
 * IntersectionObserver root on both sides of the 990px scroll-container swap.
 *
 * CONTRACT
 *   Markup alone (no JS, or reduced motion): poster + native controls, no
 *   autoplay. The parent can press play.
 *   With JS and motion allowed: controls removed; play() when the section is
 *   near the viewport, pause() when it leaves.
 *
 * play() RETURNS A PROMISE THAT CAN REJECT. iOS Low Power Mode and data-saver
 * modes block autoplay even for muted video. On rejection the controls come
 * back, so the parent is never left with a frozen poster and nothing to press.
 *
 * MORPH. Sections are re-rendered with childrenOnly morph (theme editor), and
 * updatedCallback never fires (measured, task 4). This element survives, but
 * its <video> can be replaced. A MutationObserver notices a new <video> and
 * re-applies the current state. The video is always looked up fresh, never
 * cached for use.
 */

import { Component } from '@theme/component';
import { observe } from '@theme/mk-scroll-engine';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MkWhatsInABox extends Component {
  /** @type {(() => void) | null} */
  #unobserve = null;
  /** @type {MutationObserver | null} */
  #mutations = null;
  /** Identity only, to notice a morph swapping the element. Never used to play. */
  /** @type {HTMLVideoElement | null} */
  #seenVideo = null;
  #inView = false;

  #onMotionChange = () => this.#sync();

  connectedCallback() {
    super.connectedCallback();

    this.#unobserve = observe(
      this,
      (isVisible) => {
        this.#inView = isVisible;
        this.#sync();
      },
      // Start a little before it scrolls in, so the first frames are ready.
      { rootMargin: '200px 0px 200px 0px' }
    );

    this.#mutations = new MutationObserver(() => {
      const video = this.#video();
      if (video === this.#seenVideo) return;
      this.#seenVideo = video;
      this.#sync();
    });
    this.#mutations.observe(this, { childList: true, subtree: true });

    reducedMotion.addEventListener('change', this.#onMotionChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unobserve?.();
    this.#unobserve = null;
    this.#mutations?.disconnect();
    this.#mutations = null;
    reducedMotion.removeEventListener('change', this.#onMotionChange);
    this.#video()?.pause();
  }

  /** @returns {HTMLVideoElement | null} */
  #video() {
    return this.querySelector('.mk-whats-in-a-box__frame video');
  }

  #sync() {
    const video = this.#video();
    if (!video) return; // placeholder state: nothing to drive
    this.#seenVideo = video;

    if (reducedMotion.matches) {
      // Hand control to the parent. Do not autoplay.
      video.setAttribute('controls', '');
      return;
    }

    if (!this.#inView) {
      if (!video.paused) video.pause();
      return;
    }

    // Autoplay is only permitted muted and inline; set the properties as well
    // as trusting the attributes.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;

    const attempt = video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt
        .then(() => {
          // Re-check: the section may have left the viewport while loading.
          if (this.#inView && !reducedMotion.matches) video.removeAttribute('controls');
        })
        .catch(() => {
          video.setAttribute('controls', '');
        });
    } else {
      video.removeAttribute('controls');
    }
  }
}

if (!customElements.get('mk-whats-in-a-box')) {
  customElements.define('mk-whats-in-a-box', MkWhatsInABox);
}
